import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth.ts';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetTaskByIdQuery } from 'store/api/tasks';
import { useGetCurrentTenantQuery } from 'store/api/tenants.ts';
import { TaskForm } from 'types/tasks';
import { useClassifiers } from 'utils/useClassifiers';
import { TASK_FORM_CLASSIFIERS, taskDtoToForm } from './taskFormMapping';
import TaskPageForm from './TaskPageForm';

const ClassicTaskPageEdit = (): JSX.Element => {
    const { id: taskId } = useParams();
    const { user: currentUser } = useAuth();

    const { data: task, isLoading, isError } = useGetTaskByIdQuery(taskId ?? skipToken);

    const { data: tenantData } = useGetCurrentTenantQuery();
    const emailOrganizer = tenantData?.emailOrganizer;

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: TASK_FORM_CLASSIFIERS });

    // Derived state — compute during render instead of mirroring inputs into useState via useEffect.
    const initialData = useMemo<TaskForm | undefined>(
        () => (currentUser && task ? taskDtoToForm(task, { classifiers, currentUser, emailOrganizer }) : undefined),
        [task, classifiers, currentUser, emailOrganizer]
    );

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return <TaskPageForm type="edit" classifiers={classifiers} initialData={initialData} />;
};

/** In the new design a task is edited card by card on its page, so its edit URL opens General there. */
const TaskPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: taskId } = useParams();
    return isNew ? <Navigate to={`/task/${taskId}?edit=general`} replace /> : <ClassicTaskPageEdit />;
};

export default TaskPageEdit;
